/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';

/**
 * A higher order component used to mount and unmount the component passed
 * based on the `hidden` prop. Especially useful to reset state of children component.
 * A prototypical example to use this component is to wrap modals that
 * appear and disappear based on a boolean flag.
 *
 * @param Component The component to be rendered.
 * @param delay An optional delay for un-mounting the children
 * component after the hidden flag is set to true.
 */
export const withModalMounter = <P extends { hidden?: boolean }>(Component: React.ComponentType<P>, delay: number = 200) => (props: P) => {
        const { hidden } = props;
        const [isMounted, setIsMounted] = React.useState<boolean>(!hidden);

        React.useEffect(() => {
            if (hidden) {
                const timerId = setTimeout(() => setIsMounted(false), delay);

                return () => clearTimeout(timerId);
            }
                setIsMounted(true);
        }, [hidden]);

        return isMounted ? <Component {...props} /> : null;
    };
